import {
	closestCenter,
	DndContext,
	type DragEndEvent,
	type DraggableSyntheticListeners,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type React from "react";
import { useMemo } from "react";

export type DraggableListProps<T> = {
	items: T[];
	/** Return a *stable* id for each item (string). */
	getId: (item: T, index: number) => string;
	/** Called with the items reordered. */
	onChange: (next: T[]) => void;
	/** Render a row. You get item and a `dragHandleProps` to spread onto your handle. */
	renderItem: (args: { item: T; index: number; isDragging: boolean; handleListeners: DraggableSyntheticListeners }) => React.ReactNode;
	/** Optional: customize activation constraint */
	pointerDistance?: number; // px before drag starts
	/** Optional: custom sorting strategy (defaults to vertical list) */
	strategy?: typeof verticalListSortingStrategy;
};

export function DraggableList<T>({ items, getId, onChange, renderItem, pointerDistance = 6, strategy = verticalListSortingStrategy }: DraggableListProps<T>) {
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: { distance: pointerDistance },
		}),
		useSensor(KeyboardSensor),
	);

	const ids = useMemo(() => items.map(getId), [items, getId]);

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		const oldIndex = ids.indexOf(String(active.id));
		const newIndex = ids.indexOf(String(over.id));
		if (oldIndex === -1 || newIndex === -1) return;

		onChange(arrayMove(items, oldIndex, newIndex));
	};

	return (
		<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
			<SortableContext items={ids} strategy={strategy}>
				{items.map((item, index) => (
					<SortableRow<T> key={ids[index]} id={ids[index]} item={item} index={index} renderItem={renderItem} />
				))}
			</SortableContext>
		</DndContext>
	);
}

// Internal Sortable wrapper that provides transform/drag handle props
function SortableRow<T>({ id, item, index, renderItem }: { id: string; item: T; index: number; renderItem: DraggableListProps<T>["renderItem"] }) {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

	const style: React.CSSProperties = {
		transform: CSS.Transform.toString(transform),
		transition,
	};

	return (
		<div ref={setNodeRef} style={style} {...attributes}>
			{renderItem({
				item,
				index,
				isDragging,
				handleListeners: listeners,
			})}
		</div>
	);
}
